import { useEffect, useState, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Disposable, commitMutation, fetchQuery, useMutation } from 'react-relay'
import { activeWalletUpdated, formatBalance, WalletConstant } from '../../reducers/wallet.reducer'
import ConfigurationService from '../../service/ConfigurationService'
import { store } from '../../store'
import { updateUserPreference } from '@src/queries/updateUserPreference'
import { updateUserPreferenceMutation } from '@src/queries/__generated__/updateUserPreferenceMutation.graphql'
import { formatFiat } from '../rightSide/walletComponent/WalletComponent'
import Env from '../../RelayEnvironment'
import { Currency } from '../rightSide/walletComponent/__generated__/WalletComponentlastDepositAddressQuery.graphql'
import { graphql } from 'react-relay'
import {
  DepositViewComponentDepositAddressQuery,
  DepositViewComponentDepositAddressQuery$data,
} from './__generated__/DepositViewComponentDepositAddressQuery.graphql'
import QRCode from 'react-qr-code'
import './DepositView.scoped.scss'
import { DepositViewTokensQuery, DepositViewTokensQuery$data } from './__generated__/DepositViewTokensQuery.graphql'
import Select from 'react-select'
import { DepositViewGetPermanentDepositAddressMutation } from './__generated__/DepositViewGetPermanentDepositAddressMutation.graphql'
import { getLogoForCoin } from '@src/service/coins/coins'

import { UserProp } from '@src/stores/auth/user'
import { getAuthState } from '@src/stores/auth'
import { getUserPreference, addUserPreference } from '@src/stores/auth/preferences'
import { DepositInfo } from '../Elements/DepositInfo/DepositInfo'
import { useModalHook } from './WalletModal.hook'
import { NewGameListComponentstoreUserPreferenceMutation } from '../gameListComponent/__generated__/NewGameListComponentstoreUserPreferenceMutation.graphql'
import { WebpLazyImage } from '../WebpLazyImage/WepbLazyImage'

export const DepositView = ({ openListView }) => {
  const { t } = useTranslation()
  const walletStore = store.getState().wallet
  const [activeWallet, setActiveWallet] = useState(walletStore?.activeWallet)
  const [network, setNetwork] = useState<string>()

  const {
    selectedExternalCoin,
    setSelectedExternalCoin,
    isExternalCurrency,
    depositAddressData,
    setDepositAddressData,
    tokens,
    setTokens,
    coin,
    paymentOrder,
    setPaymentOrder,
  } = useModalHook()
  const [commit] = useMutation<NewGameListComponentstoreUserPreferenceMutation>(updateUserPreference)
  const [generatePermanentPaymentAddress] = useMutation<DepositViewGetPermanentDepositAddressMutation>(
    getPermanentDepositAddressMutation
  )

  useEffect(() => {
    fetchQuery<DepositViewTokensQuery>(Env, listTokens, {}).subscribe({
      next: (data) => {
        setTokens(data)
        if (isExternalCurrency && activeWallet) {
          const token = data.tokens.find((x) => x.crypto === activeWallet.currency)
          if (!token) {
            return
          }
          let defaultToken
          let defaultChain
          if (token.tokens.length === 1) {
            defaultToken = token.tokens[0].tokenId
            defaultChain = token.tokens[0].chain
          }
          setSelectedExternalCoin({ coin: token.coinId, token: defaultToken, chain: defaultChain })
          setDepositAddressData(undefined)
          setNetwork(undefined)
        }
      },
    })
  }, [activeWallet, isExternalCurrency])

  useEffect(() => {
    // We we are using external currencies then we also need network
    if (activeWallet && isExternalCurrency && (network?.length || 0) === 0) {
      return
    }
    if (activeWallet?.currency === 'JACK') {
      // Imaginary coin
      return
    }
    fetchQuery<DepositViewComponentDepositAddressQuery>(Env, getDepositAddressQuery, {
      currency: activeWallet!.currency as Currency,
    }).subscribe({
      next: (data) => {
        if (data.lastDepositAddress?.currency !== activeWallet?.currency) {
          return
        }
        if (data.lastDepositAddress) {
          setDepositAddressData(data)
        }
      },
    })
  }, [activeWallet, network, isExternalCurrency])

  useEffect(() => {
    const subscription = activeWalletUpdated.subscribe(() => {
      setActiveWallet(store.getState().wallet.activeWallet)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [activeWallet])

  useEffect(() => {
    let query: Disposable
    if (selectedExternalCoin?.coin && selectedExternalCoin.token) {
      setDepositAddressData(undefined)
      query = generatePermanentPaymentAddress({
        variables: { input: { tokenId: selectedExternalCoin.token, chainId: selectedExternalCoin.chain } },
        onCompleted: (resp) => {
          if (!resp.getPermanentDepositAddress) {
            return
          }
          setPaymentOrder({
            address: resp.getPermanentDepositAddress.payAddress,
            memo: resp.getPermanentDepositAddress.memo,
          })
          setDepositAddressData({
            lastDepositAddress: {
              address: resp.getPermanentDepositAddress.payAddress,
              currency: undefined as any,
              isShared: false,
              expireTime: null,
            },
          })
        },
      })
    }
    return () => {
      query?.dispose()
    }
  }, [selectedExternalCoin, generatePermanentPaymentAddress])

  if (!activeWallet) {
    return <span>...</span>
  }

  const coins = ConfigurationService.instance.getCurrencies()
  const unitConfig = coins[activeWallet.currency]
  const defaultUnit = ConfigurationService.instance.getCurrency(activeWallet.currency).format
  const selectedUnit = getUserPreference(
    ('unit_' + activeWallet.currency.toUpperCase()) as UserProp,
    (value) => value || defaultUnit
  )

  const setActive = (e: React.MouseEvent | undefined, currency: string) => {
    e?.preventDefault()
    e?.stopPropagation()

    const w = store
      .getState()
      .wallet.wallets.find((x) => x.currency.toLowerCase() === currency.toLowerCase() && x.virtualId === 0)
    if (!w) return

    store.dispatch({ type: WalletConstant.SET_ACTIVE_WALLET, wallet: w })
    commitMutation<updateUserPreferenceMutation>(Env, {
      mutation: updateUserPreference,
      variables: { input: { key: UserProp.ACTIVE_COIN, value: w.currency } },
    })
  }

  const setActiveUnit = (e: React.MouseEvent, currency: string, unit: string) => {
    e.preventDefault()
    e.stopPropagation()
    setActive(undefined, currency)
    commit({
      variables: { input: { key: 'unit_' + currency, value: unit } },
      onCompleted: (resp) => {
        if (!resp.storeUserPreference) return
        const { preference } = resp.storeUserPreference

        const state = getAuthState()
        switch (state.type) {
          case 'LoggedIn':
            addUserPreference(preference)
            break
          default:
            throw new Error('Not logged in')
        }
      },
    })
  }

  return (
    <div className="">
      <div className="dep-table">
        <div>
          <span>{t('wallet.balance', 'Balance')}</span>
          <span className="coin-header">{t('wallet.coin', 'Coin')}</span>
        </div>
        <div className="balance-row">
          <div className="balance">
            <span>{formatBalance(activeWallet, false, false)}</span>
            {formatFiat(walletStore.rates, activeWallet)}
          </div>
          <div>
            <div className="select" onClick={openListView}>
              <div className="currency">
                <WebpLazyImage
                  className="coin-image"
                  alt={activeWallet.currency}
                  src={getLogoForCoin(activeWallet.currency)}
                />
                {activeWallet.currency}
              </div>
              <FontAwesomeIcon icon={faChevronDown} />
            </div>
          </div>
        </div>
      </div>
      <div className="deposit-units">
        <span className="label">{t('wallet.unit', 'Unit')}</span>
        <div className="unit-list">
          {Object.keys(unitConfig.units).map((key) => {
            const unit = unitConfig.units[key]
            if (unit.code === 'raw') return null

            return (
              <span
                onClick={(e) => setActiveUnit(e, activeWallet.currency, unit.code)}
                className={'wallet-unit' + (unit.code === selectedUnit ? ' selected' : '')}
                title={unit.name}
                key={'unit_' + unit.name}
              >
                {unit.code}
              </span>
            )
          })}
        </div>
      </div>
      <DepositInfo
        isExternalCurrency={!!isExternalCurrency}
        selectedExternalCoin={selectedExternalCoin}
        depositAddressData={depositAddressData}
        coin={coin}
        paymentOrder={paymentOrder}
        onNetworkSelect={(tokenId, chain) =>
          setSelectedExternalCoin({ coin: selectedExternalCoin?.coin, token: tokenId, chain })
        }
      />
    </div>
  )
}

export const getDepositAddressQuery = graphql`
  query DepositViewComponentDepositAddressQuery($currency: Currency!) {
    lastDepositAddress(currency: $currency) {
      currency
      address
      isShared
      expireTime
    }
  }
`

export const listTokens = graphql`
  query DepositViewTokensQuery {
    tokens {
      symbol
      crypto
      name
      logo
      min
      price
      coinId
      status
      tokens {
        tokenId
        crypto
        logo
        name
        network
        chain
        contract
        chainLogo
        status
      }
    }
  }
`

// This will generate new deposit order, it will be valid for about 24 hours
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const generatePaymentOrderMutation = graphql`
  mutation DepositViewGeneratePaymentOrderMutation($input: GeneratePaymentOrderInput!) {
    generatePaymentOrder(input: $input) {
      payAddress
      memo
    }
  }
`

// This will generate permanent deposit address for user, it will never expire
export const getPermanentDepositAddressMutation = graphql`
  mutation DepositViewGetPermanentDepositAddressMutation($input: GetPermanentDepositAddressInput!) {
    getPermanentDepositAddress(input: $input) {
      payAddress
      memo
    }
  }
`
